<template>
  <div class="calico-subscription-card">
    <div class="content-container">
      <!-- Tier Container -->
      <div class="tier-container">
        <!-- Tier Label -->
        <h3 class="tier-label heading--medium">
          {{ tier }}
        </h3>

        <!-- Pricing Container -->
        <div
          class="pricing-container"
          :class="{ split: formattedInfoCards.length > 1 }"
        >
          <CalicoCardInfo
            v-for="(card) in formattedInfoCards"
            :key="card.label"
            :label="card.label"
            :content="card.content"
          />
        </div>

        <!-- Features Container -->
        <div
          v-if="features.length"
          class="features-container"
        >
          <ul>
            <li
              v-for="(feature) in features"
              :key="feature"
              class="feature-item"
            >
              <span class="body--medium">
                {{ feature }}
              </span>
            </li>
          </ul>
        </div>

        <!-- Cancellation Message -->  
        <div
          v-if="cancellationMessage"
          class="cancellation-message body--small"
        >
          {{ cancellationMessage }}
        </div>
      </div>

      <!-- Billing Cycle Container -->
      <div
        v-if="nextBillingDate"
        class="billing-cycle-container"
      >
        <div class="billing-label body--small">
          {{ billingLabel }}
        </div>

        <div class="billing-frame body--small">
          {{ billingPrefix ? `${billingPrefix} on` : 'on' }} {{ formattedBillingDate }}        
        </div>
      </div>

      <!-- CTA Container -->
      <div
        v-if="buttons.length"
        class="cta-container"
      >
        <button
          v-for="(btn) in buttons"
          :key="btn.label"
          type="button"
          :class="resolveButtonClass(btn)"
          @click="$emit(btn.event)"
        >
          {{ btn.label }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import CalicoCardInfo from '@/components/calico/cardInfo/index.vue';
import { formatDisplayDate, formatCurrency } from '@/utils/helper';
export default {
  name: "CalicoSubscriptionCard",
  components: {
    CalicoCardInfo, 
  },
  props: {
    tier: {
      type: String,
      default: ''
    },

    infoCards: {
      type: Array,
      default: () => []
    },

    features: {
      type: Array,
      default: () => []
    },

    nextBillingDate: {
      type: String,
      default: ''
    },

    billingPrefix: {
      type: String,
      default: ''
    },

    billingLabel: {
      type: String,
      default: 'Next Billing Cycle:'
    },

    buttons: {
      type: Array,
      default: () => []
    },

    cancellationMessage: {
      type: String,
      default: null
    }
  },

  emits: ['manage', 'upgrade', 'reactivate'],
  computed: {
    formattedBillingDate() {
      return formatDisplayDate(this.nextBillingDate) || this.nextBillingDate;
    },
    formattedInfoCards() {
      return this.infoCards.map((card) => ({
        ...card,
        content: formatCurrency(card.content),
      }));
    },
  },
  methods: {
    resolveButtonClass(btn) {
      if (btn.variant === 'secondary') {
        return 'button-secondary button-secondary--small'
      }

      return 'button-primary button-primary--small'
    }
  },
};
</script>

<style lang="scss">
.calico-subscription-card {
  @include inset-space($space-large);
  border: 1px solid $color-neutral-light;
  border-radius: $radius-large;
  background: $color-white;

  .content-container {
    @include stack-space($space-large);

    .tier-container {
      @include stack-space($space-medium);

      .tier-label {
        font-weight: $font-weight-bold;
      }

      .pricing-container {
        @include inset-space($space-medium);
        border-radius: $radius-medium;

        &.split {
          display: flex;
          gap: $space-small;
        }

        &.split > * {
          flex: 1;
        }
        & div.content {
          font-weight: $font-weight-bold;
        }
      }

      .features-container {
        ul {
          list-style-type: square;
          list-style-position: outside;
          padding-left: $space-large;
          margin: 0;
        }

        .feature-item {
          display: list-item;
          margin-bottom: $space-xsmall;
        }
      }

      .cancellation-message {
        color: $color-semantics-error;
      }
    }

    .billing-cycle-container {
      @include stack-space($space-xxsmall);
      color: $color-text-secondary;
    }

    .billing-frame {
      @include inline-space($space-xsmall);
      align-items: center;
    }

    .cta-container {
      @include inline-space($space-small);
      gap: $space-small;
      align-items: center;
      flex-wrap: wrap; 

      > button {
        width: fit-content;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        white-space: nowrap;
      }
      .button-primary {
        border: solid $color-transparent $border-width-default;
      }
    }
  }
}
</style>